import type { Node } from "@xyflow/react";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useFramesStore } from "@/stores/frames-store";
import { useProjectStore } from "@/stores/project-store";
import {
  tidyFramesByTimePage,
  type TidyUpdate,
} from "@/components/layout/tidy-frames";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useReviewModeStore } from "@/stores/review-mode-store";
import { isWorkspaceFrame } from "@/lib/frame-classification";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import {
  getWindowDisplayId,
  getWindowRoleSync,
} from "@/desktop/use-window-role";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { isDisplayWindow } from "@/desktop/setup-window-surface";
import { presentCursor } from "@/lib/canvas-cursor-presentation";
import {
  enterAgentFocusPresentation,
  matchingFocusPresentationIds,
  presentAgentRound,
} from "@/lib/agent-focus-presentation";
import { shouldUseDesktopExpandedPresentation } from "@/lib/focus/focus-desktop-mode";
import type { PresentFramesRequest } from "@/lib/canvas-presentation-types";
import {
  withMutationEmitEnabled,
  withMutationEmitSuppressed,
  withStoreKeptOnMirror,
} from "@/lib/mirrored-store";
import {
  exitPresentationMode,
  scrubPresentationClasses,
} from "@/lib/presentation-mode";
import { fitTidiedFrames } from "@/lib/canvas-presentation-fit-return";
import { cancelPendingConversationFrameReveals } from "@/lib/conversation-frame-reveal";
import { removeTransientConversationFramesFromCanvas } from "@/lib/transient-conversation-frame-cleanup";
import type { FrameData } from "@/lib/frame-data";
import { persistFrameGeometryUpdates } from "@/lib/frame-geometry-persistence";
import { focusRendererKind } from "@/lib/focus/focus-renderer-kind";
import { flashWallArrival } from "@/lib/wall-arrivals";
import { placeKeptFrames } from "@/components/canvas/canvas-kept-frames";
import { showFramesLocally } from "@/lib/frame-visibility";
import {
  focusRoundBefore,
  notePresentedCards,
  releasePresenterCards,
  traceFocusRound,
} from "@/lib/focus/agent-presented-cards";

export type {
  PresentFramesRequest,
  PresentPlacement,
  PresentPoint,
} from "@/lib/canvas-presentation-types";

useDesktopMode.subscribe((state, prev) => {
  // Opening the Workspace ends compact focus; a canvas capture also ends
  // canvas focus, while focus over the wall stays drawn (Part 05 decision 8).
  const focus = usePresentationStore.getState().presentation;
  if (
    focus?.variant === "focus" &&
    state.mode === "denk" &&
    (prev.mode !== "denk" ||
      (focus.renderer === "canvas" &&
        state.denkCanvasCapture &&
        !prev.denkCanvasCapture))
  ) {
    exitPresentationMode();
  }
  if (prev.mode !== "peek" || state.mode === "peek") return;
  const canvasStore = useCanvasStore.getState();
  if (canvasStore.focusHiddenFrameIds.size === 0) return;
  if (usePresentationStore.getState().presentation?.variant === "focus") {
    const restore = canvasStore.presentationRestore;
    if (restore) {
      useCanvasStore.setState({
        presentationRestore: { ...restore, hiddenFrameIds: [] },
      });
    }
    return;
  }
  canvasStore.setFocusHiddenFrames([]);
});

function applyTidiedNodes(
  nodes: Node<FrameData>[],
  updateMap: Map<string, TidyUpdate>,
  spaceId: string | null,
) {
  return nodes.map((node) => {
    if (!isWorkspaceFrame(node.data.frameType)) return node;
    if (spaceId && node.data.spaceId !== spaceId) return node;
    const update = updateMap.get(node.id);
    if (!update) {
      return {
        ...node,
        hidden: false,
        className: scrubPresentationClasses(node.className),
      };
    }
    return {
      ...node,
      hidden: false,
      className: scrubPresentationClasses(node.className),
      position: { x: update.x, y: update.y },
      width: update.w,
      height: update.h,
      style: { ...node.style, width: update.w, height: update.h },
      measured: { width: update.w, height: update.h },
    };
  });
}

function nodeMatchesTidyUpdate(node: Node<FrameData>, update: TidyUpdate) {
  const size = frameNodeSize(node);
  return (
    node.position.x === update.x &&
    node.position.y === update.y &&
    size.width === update.w &&
    size.height === update.h
  );
}
function persistableTidyUpdates(
  nodes: Node<FrameData>[],
  updateMap: Map<string, TidyUpdate>,
) {
  return nodes.flatMap((node) => {
    const update = updateMap.get(node.id);
    const wasDurablyHidden = !!node.data.metadata?.hidden;
    const geometryChanged = !!update && !nodeMatchesTidyUpdate(node, update);
    if (!geometryChanged && !wasDurablyHidden) return [];
    return {
      id: node.id,
      ...(geometryChanged &&
        update && {
          position_x: update.x,
          position_y: update.y,
          width: update.w,
          height: update.h,
        }),
      ...(wasDurablyHidden && {
        metadata: { ...node.data.metadata, hidden: false },
      }),
    };
  });
}
export function presentFrames(request: PresentFramesRequest) {
  const ids = request.frameIds.filter(Boolean);
  // The display's own window presents; a second one on the same display
  // (the setup window) would end and restart the Focus it shows.
  if (ids.length === 0 || !isDisplayWindow()) return;
  const myDisplayId = getWindowDisplayId();
  const targetDisplayId =
    request.targetDisplayId ??
    request.cursorPoint?.displayId ??
    useActiveDisplay.getState().activeDisplayId;
  if (
    targetDisplayId != null &&
    myDisplayId != null &&
    targetDisplayId !== myDisplayId
  ) {
    presentCursor(request);
    return;
  }
  const before = focusRoundBefore(request);
  if (targetDisplayId == null || myDisplayId !== targetDisplayId) {
    presentFramesHere(request, ids, targetDisplayId, false);
  } else {
    // Only this window shows a display-targeted presentation, so the other
    // windows hear it even while this one applies a relayed frame (whose
    // writes stay unsent): the owner reports what the screen shows. As when
    // the frames arrive later, a mirror's canvas nodes stay its own.
    withMutationEmitEnabled(() =>
      withStoreKeptOnMirror("canvas", () =>
        presentFramesHere(request, ids, targetDisplayId, true),
      ),
    );
  }
  traceFocusRound(request, before);
}

function presentFramesHere(
  request: PresentFramesRequest,
  ids: string[],
  targetDisplayId: number | null,
  allowMirrorDesktopFocus: boolean,
) {
  const visibility = request.visibility ?? "show_only";
  const { framesById } = useFramesStore.getState();
  const requestedExistingIds = ids.filter((id) => !!framesById[id]);
  const activeFrame = framesById[requestedExistingIds[0] ?? ""];
  if (!activeFrame) return;
  releasePresenterCards(request, requestedExistingIds);
  notePresentedCards(request, requestedExistingIds);
  const conversationFamily = activeFrame.frameType === "conversation";
  const existingIds = requestedExistingIds.filter(
    (id) =>
      (framesById[id]!.frameType === "conversation") === conversationFamily,
  );

  const isDesktopPresentation = shouldUseDesktopExpandedPresentation();
  const desktopMode = useDesktopMode.getState();
  const { presentation } = usePresentationStore.getState();
  const focusPresentationIds =
    presentation?.variant === "focus" ? presentation.frameIds : [];
  const hasActiveCompactFocusFrame = desktopMode.compactFrameOverlayIds.some(
    (id) => focusPresentationIds.includes(id),
  );
  // A running Compact focus takes the request as a round (plan 13).
  if (
    isDesktopPresentation &&
    desktopMode.mode === "compact" &&
    hasActiveCompactFocusFrame
  ) {
    useReviewModeStore.getState().closeReviewMode();
    if (
      presentAgentRound(existingIds, request, {
        allowMirrorDesktopFocus,
      })
    ) {
      const focusedIds =
        usePresentationStore.getState().presentation?.frameIds ?? existingIds;
      presentCursor({ ...request, exclusionFrameIds: focusedIds });
      return;
    }
  }

  // Desktop frame replies stay in Compact focus. `keep` is additive only
  // when a Compact focus already runs; otherwise it behaves like show_only.
  const shouldEnterFocusPresentation =
    visibility !== "keep" || isDesktopPresentation;

  if (shouldEnterFocusPresentation) {
    const preservedFocusIds = matchingFocusPresentationIds(existingIds);
    useReviewModeStore.getState().closeReviewMode();
    enterAgentFocusPresentation(existingIds, request, {
      targetDisplayId,
      allowMirrorDesktopFocus,
      preservedFocusIds,
      // Rounds belong to desktop Compact focus, like `presentAgentRound`.
      asRound: isDesktopPresentation && desktopMode.mode === "compact",
    });
    presentCursor({ ...request, exclusionFrameIds: existingIds });
    return;
  }

  exitPresentationMode();
  useReviewModeStore.getState().closeReviewMode();
  useSelectionStore.setState((current) => ({
    selectedFrameId: existingIds[0] ?? current.selectedFrameId,
    multiSelectedFrameIds: existingIds.length > 1 ? existingIds : [],
    selectionSeq: current.selectionSeq + 1,
  }));
  if (focusRendererKind() === "overlay") {
    flashWallArrival(existingIds);
    presentCursor({ ...request, exclusionFrameIds: existingIds });
    return;
  }
  placeKeptFrames(existingIds, request, {
    targetDisplayId,
    allowMirrorDesktopFocus,
  });
}
export function showAllFramesTidied() {
  cancelPendingConversationFrameReveals();
  let state = useCanvasStore.getState();
  const fitSpaceId = useProjectStore.getState().currentSpace?.id ?? null;
  if (usePresentationStore.getState().presentation) {
    exitPresentationMode({ restoreViewport: false });
    state = useCanvasStore.getState();
  }
  if (removeTransientConversationFramesFromCanvas())
    state = useCanvasStore.getState();
  const scopedNodes = state.nodes.filter(
    (node) =>
      isWorkspaceFrame(node.data.frameType) &&
      (!fitSpaceId || node.data.spaceId === fitSpaceId),
  );
  // Fit view ("show all, tidied") only unhides + repositions: it must keep
  // each frame's current size (e.g. user-resized widgets). The tidy packer
  // sizes from the live nodes, so resetting to defaults here would snap
  // custom sizes back to 400x300 and persist the defaults to the backend.
  const allShownNodes = scopedNodes.map((node) => {
    const size = frameNodeSize(node);
    return {
      ...node,
      hidden: false,
      width: size.width,
      height: size.height,
      style: { ...node.style, width: size.width, height: size.height },
      measured: { width: size.width, height: size.height },
      // Layout input only: tidy places every scoped frame as shown.
      data: {
        ...node.data,
        metadata: { ...node.data.metadata, hidden: false },
      },
    };
  });
  const updates = tidyFramesByTimePage(allShownNodes);
  const updateMap = new Map(updates.map((u) => [u.id, u]));

  if (getWindowRoleSync() === "mirror") {
    const desktopMode = useDesktopMode.getState();
    if (desktopMode.mode === "denk") {
      useDesktopMode.setState((current) => ({
        denkCanvasScope: "single-monitor",
        denkCanvasScopeSeq: current.denkCanvasScopeSeq + 1,
        denkCanvasFocusTarget: null,
      }));
    }
    useCanvasStore.getState().setFocusHiddenFrames([]);
    usePresentationStore.getState().setPresentation(null);
    withMutationEmitSuppressed(() => {
      showFramesLocally(scopedNodes.map((node) => node.id));
      useCanvasStore.setState((current) => ({
        nodes: applyTidiedNodes(current.nodes, updateMap, fitSpaceId),
      }));
    });
    persistFrameGeometryUpdates(persistableTidyUpdates(scopedNodes, updateMap));
    fitTidiedFrames(fitSpaceId);
    return;
  }

  if (
    updates.length === 0 &&
    !scopedNodes.some((node) => !!node.data.metadata?.hidden)
  ) {
    useCanvasStore.getState().setFocusHiddenFrames([]);
    usePresentationStore.getState().setPresentation(null);
    const desktopMode = useDesktopMode.getState();
    if (desktopMode.mode === "denk") {
      desktopMode.setDenkCanvasScope("single-monitor");
    }
    fitTidiedFrames(fitSpaceId);
    return;
  }

  showFramesLocally(scopedNodes.map((node) => node.id));
  useCanvasStore.setState((current) => ({
    focusHiddenFrameIds: new Set<string>(),
    nodes: applyTidiedNodes(current.nodes, updateMap, fitSpaceId),
  }));
  usePresentationStore.getState().setPresentation(null);

  const desktopMode = useDesktopMode.getState();
  if (desktopMode.mode === "denk") {
    desktopMode.setDenkCanvasScope("single-monitor");
  }

  fitTidiedFrames(fitSpaceId);

  persistFrameGeometryUpdates(persistableTidyUpdates(scopedNodes, updateMap));
}
